import { ArrowRight, KeyRound } from 'lucide-react';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { OTP_UI } from '@/lib/constants/otp';
import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/ui/OtpInput';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';

const F = UI_STRINGS.FORGOT_PASSWORD;
const ARROW_SIZE = 18;

export interface ForgotPasswordCodeStepProps {
  code: string;
  onCodeChange: (value: string) => void;
  codeError?: string;
  /** Client-side-only advance to the password step - see ForgotPasswordPage. */
  onContinue: () => void;
  onResend: () => void;
  onUseDifferentEmail: () => void;
  isResendPending: boolean;
  cooldownExpired: boolean;
  resendErrorMessage?: string;
  resent: boolean;
}

/**
 * The reset flow's code step: OTP entry plus the Resend control and its
 * countdown. Advancing (`onContinue`) only checks the code's shape/length
 * here - see ForgotPasswordPage's handleContinueToPassword for why it makes
 * no network call.
 */
export function ForgotPasswordCodeStep({
  code,
  onCodeChange,
  codeError,
  onContinue,
  onResend,
  onUseDifferentEmail,
  isResendPending,
  cooldownExpired,
  resendErrorMessage,
  resent,
}: ForgotPasswordCodeStepProps) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onContinue();
      }}
      noValidate
      className="flex flex-col gap-6"
    >
      <SectionCard title={F.CODE_LABEL} icon={<KeyRound size={20} />}>
        <div className="flex flex-col items-center gap-4 py-2">
          <OtpInput
            label={F.CODE_LABEL}
            value={code}
            onChange={onCodeChange}
            length={OTP_UI.CODE_LENGTH}
            error={codeError}
          />
          <div className="flex flex-col items-center gap-3 text-center">
            {resendErrorMessage && (
              <p role="alert" className="text-sm text-danger">
                {resendErrorMessage}
              </p>
            )}
            <p className="text-sm text-subtext">
              {F.NO_CODE}{' '}
              <Button
                type="button"
                variant="link"
                disabled={!cooldownExpired || isResendPending}
                onClick={onResend}
                className="align-baseline disabled:text-slate"
              >
                {isResendPending ? F.RESENDING : F.RESEND}
              </Button>
            </p>
            {resent && (
              <p role="status" className="text-sm text-dkgreen">
                {F.RESENT}
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      <div className="flex flex-col gap-4">
        <ShieldNote>{F.ENCRYPTION_NOTE}</ShieldNote>
        <div className="flex items-center justify-between gap-6">
          <Button
            type="button"
            variant="link"
            onClick={onUseDifferentEmail}
            className="align-baseline"
          >
            {F.USE_DIFFERENT_EMAIL}
          </Button>
          <Button
            type="submit"
            variant="navy"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
          >
            {F.CODE_CONTINUE}
            <ArrowRight size={ARROW_SIZE} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </form>
  );
}
